import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, Languages, Tag, X, Phone, MessageSquare, MessageSquareQuote, CalendarClock, Bot, TrendingUp, Sparkles, UserCheck, Volume2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useBusiness } from "@/context/BusinessContext";
import { useLearning } from "@/context/LearningContext";
import { Topbar } from "@/components/Topbar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const LANG_FLAG = { ar: "🇸🇦", en: "🇬🇧", unknown: "🌐" };
function LangFlag({ lang, className = "" }) {
  const l = lang === "ar" || lang === "en" ? lang : "unknown";
  return (
    <span
      data-testid={`lang-badge-${l}`}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${l === "unknown" ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"} ${className}`}
    >
      {LANG_FLAG[l]} {l === "unknown" ? "?" : l.toUpperCase()}
    </span>
  );
}

const LEAD_STATUSES = ["new", "qualified", "follow_up", "won", "lost"];
const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  qualified: "bg-purple-100 text-purple-700",
  follow_up: "bg-amber-100 text-amber-700",
  won: "bg-green-100 text-green-700",
  lost: "bg-rose-100 text-rose-700",
};

function ListenButton({ text, lang, testid }) {
  const { t } = useI18n();
  const [playing, setPlaying] = React.useState(false);
  const listen = async () => {
    if (playing) return;
    setPlaying(true);
    try {
      const { data } = await api.post("/tts", { text, lang });
      const a = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      a.onended = () => setPlaying(false);
      await a.play();
    } catch { setPlaying(false); }
  };
  return (
    <button onClick={listen} disabled={playing} title={t("listenLabel")} aria-label={t("listenLabel")} data-testid={testid}
      className="inline-flex items-center gap-1 text-xs text-primary/70 hover:text-primary mt-1.5 me-3">
      {playing ? <Loader2 size={12} className="animate-spin" /> : <Volume2 size={12} />} {t("listenLabel")}
    </button>
  );
}

export default function Inbox() {
  const { t } = useI18n();
  const { business } = useBusiness();
  const { learn } = useLearning();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [replyLang, setReplyLang] = useState("ar");
  const [sending, setSending] = useState(false);
  const [translatingId, setTranslatingId] = useState(null);
  const [notes, setNotes] = useState("");
  const [newTag, setNewTag] = useState("");
  const [savedReplies, setSavedReplies] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [leadStatus, setLeadStatus] = useState("new");
  const [followUp, setFollowUp] = useState("");
  const [apptAt, setApptAt] = useState("");
  const [apptNote, setApptNote] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [draftLang, setDraftLang] = useState("en");
  const [fromDraft, setFromDraft] = useState(false);
  const endRef = useRef(null);

  const statusLabel = (s) => t(`status${s.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join("")}`);

  const fetchConversations = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load conversations");
      return [];
    }
  };

  const openConversation = async (id) => {
    if (id === activeId) return;
    setActiveId(id);
    setShowSaved(false);
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setConv(data.conversation);
      setMessages(data.messages);
      setNotes(data.conversation.notes || "");
      setReplyLang(data.conversation.customer_lang || "ar");
      setLeadStatus(data.conversation.lead_status || "new");
      setFollowUp(data.conversation.follow_up_at || "");
      setApptAt(data.conversation.appointment_at || "");
      setApptNote(data.conversation.appointment_note || "");
      setConversations((cs) => cs.map((c) => (c.conversation_id === id ? { ...c, unread: false, preferred_language: data.conversation.preferred_language } : c)));
      fetchDrafts(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open conversation");
    }
  };

  const fetchDrafts = async (id) => {
    setDrafts([]); setFromDraft(false);
    try {
      const { data } = await api.get(`/conversations/${id}/drafts`);
      setDrafts(data.drafts || []);
      setDraftLang(data.lang || "en");
    } catch { /* drafts are best-effort */ }
  };

  const setContactLanguage = async (lang) => {
    try {
      const { data } = await api.put(`/conversations/${activeId}/language`, { preferred_language: lang });
      setConv(data);
      setConversations((cs) => cs.map((c) => (c.conversation_id === activeId ? { ...c, preferred_language: data.preferred_language, tags: data.tags } : c)));
      toast.success(t("saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update language");
    }
  };

  const openedRef = useRef(false);
  useEffect(() => {
    (async () => {
      const data = await fetchConversations();
      api.get("/saved-replies").then(({ data }) => setSavedReplies(data)).catch(() => {});
      if (!openedRef.current && data.length) {
        openedRef.current = true;
        openConversation(data[0].conversation_id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const translateMessage = async (m) => {
    setTranslatingId(m.message_id);
    const target = m.lang === "ar" ? "en" : "ar";
    try {
      const { data } = await api.post(`/messages/${m.message_id}/translate`, { text: m.text, target_lang: target });
      setMessages((ms) => ms.map((x) => x.message_id === m.message_id ? { ...x, translation: data.translation, translated_lang: data.translated_lang } : x));
      learn({ source_text: data.source_text, translation: data.translation, detected_language: data.detected_language, target_lang: data.translated_lang });
    } catch (e) {
      toast.error("Translation failed");
    } finally {
      setTranslatingId(null);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${activeId}/reply`, { text: reply, lang: replyLang, from_draft: fromDraft });
      setMessages((ms) => (ms.some((x) => x.message_id === data.message_id) ? ms : [...ms, data]));
      setReply("");
      setFromDraft(false);
      setDrafts([]);
      fetchConversations();
    } catch {
      toast.error("Failed to send");
    } finally {
      setSending(false);
    }
  };

  const saveCrm = async (patch) => {
    try {
      const { data } = await api.put(`/conversations/${activeId}/crm`, patch);
      setConv(data);
      fetchConversations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save changes");
    }
  };

  const addTag = () => {
    if (!newTag.trim() || !conv) return;
    saveCrm({ tags: [...(conv.tags || []), newTag.trim()] });
    setNewTag("");
  };
  const removeTag = (tag) => saveCrm({ tags: (conv.tags || []).filter((x) => x !== tag) });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Topbar />
      <div className="flex-1 max-w-7xl w-full mx-auto grid grid-cols-1 md:grid-cols-12 gap-0 md:gap-4 md:p-4">
        {/* Conversation list */}
        <aside className="md:col-span-3 bg-white md:rounded-2xl border-border md:border overflow-hidden flex flex-col max-h-[calc(100vh-6rem)]">
          <div className="p-4 border-b border-border font-head font-semibold text-foreground">{t("conversations")}</div>
          <div className="overflow-y-auto scroll-thin flex-1">
            {conversations.map((c) => (
              <button
                key={c.conversation_id}
                onClick={() => openConversation(c.conversation_id)}
                data-testid={`conversation-${c.conversation_id}`}
                className={`w-full text-start p-4 border-b border-border hover:bg-secondary/50 transition-colors ${activeId === c.conversation_id ? "bg-secondary" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground truncate">{c.customer_name}</span>
                  <LangFlag lang={c.preferred_language || c.customer_lang} className="shrink-0" />
                </div>
                <p className="text-sm text-muted-foreground truncate mt-0.5" dir={(c.preferred_language || c.customer_lang) === "ar" ? "rtl" : "ltr"}>{c.last_message}</p>
                {c.unread && <span className="inline-block mt-1 h-2 w-2 rounded-full bg-accent" />}
              </button>
            ))}
          </div>
        </aside>

        {/* Chat thread */}
        <main className="md:col-span-6 bg-white md:rounded-2xl border-border md:border flex flex-col max-h-[calc(100vh-6rem)]">
          {!conv ? (
            <div className="flex-1 grid place-items-center text-muted-foreground">{t("selectConversation")}</div>
          ) : (
            <>
              <div className="p-4 border-b border-border flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary grid place-items-center font-semibold">
                  {conv.customer_name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground flex items-center gap-2">
                    <span className="truncate">{conv.customer_name}</span>
                    <LangFlag lang={conv.preferred_language || conv.customer_lang} />
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1"><Phone size={11} /> {conv.customer_phone}</span>
                    {conv.assigned_agent_name && (
                      <span className="flex items-center gap-1" data-testid="assigned-agent"><UserCheck size={11} /> {conv.assigned_agent_name}</span>
                    )}
                  </div>
                </div>
                <Select value={(conv.preferred_language === "ar" || conv.preferred_language === "en") ? conv.preferred_language : "unknown"} onValueChange={setContactLanguage}>
                  <SelectTrigger className="h-9 w-[110px] shrink-0" data-testid="contact-lang-select" title={t("contactLanguage")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unknown" data-testid="contact-lang-unknown">🌐 {t("langAuto")}</SelectItem>
                    <SelectItem value="ar" data-testid="contact-lang-ar">🇸🇦 AR</SelectItem>
                    <SelectItem value="en" data-testid="contact-lang-en">🇬🇧 EN</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 chat-pattern overflow-y-auto scroll-thin p-4 space-y-3">
                {messages.map((m) => {
                  const isAgent = m.sender === "agent";
                  return (
                    <div key={m.message_id} className={`flex ${isAgent ? "justify-end" : "justify-start"}`}>
                      <div
                        dir={m.lang === "ar" ? "rtl" : "ltr"}
                        className={`max-w-[80%] rounded-2xl p-3 shadow-sm ${isAgent ? "bg-[#D1F4CC] rounded-ee-sm" : "bg-white border border-border rounded-ss-sm"}`}
                      >
                        <p className="text-[15px] text-foreground leading-relaxed">{m.text}</p>
                        {m.translation && (
                          <div className="mt-2 pt-2 border-t border-black/10">
                            {!isAgent && business?.auto_translate_incoming && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-accent bg-accent/10 px-1.5 py-0.5 rounded mb-1" data-testid="auto-translated-badge">
                                <Bot size={10} /> {t("autoTranslated")}
                              </span>
                            )}
                            <p className="text-sm text-accent leading-relaxed" dir={m.translated_lang === "ar" ? "rtl" : "ltr"}>
                              {m.translation}
                            </p>
                          </div>
                        )}
                        {!isAgent && !m.translation && (
                          <button
                            onClick={() => translateMessage(m)}
                            data-testid={`translate-msg-${m.message_id}`}
                            className="flex items-center gap-1 text-xs text-primary/70 hover:text-primary mt-1.5"
                          >
                            {translatingId === m.message_id ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
                            {m.lang === "ar" ? t("translateToEn") : t("translateToAr")}
                          </button>
                        )}
                        {(() => {
                          const arText = m.lang === "ar" ? m.text : (m.translated_lang === "ar" ? m.translation : null);
                          return arText ? <ListenButton text={arText} lang="ar" testid={`listen-msg-${m.message_id}`} /> : null;
                        })()}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              {/* AI reply drafts */}
              {drafts.length > 0 && (
                <div className="px-4 pt-3 border-t border-border" data-testid="ai-drafts-row">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-accent mb-2 font-semibold">
                    <Sparkles size={13} /> {t("aiDrafts")}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {drafts.map((d, i) => (
                      <button
                        key={i}
                        onClick={() => { setReply(d); setReplyLang(draftLang === "ar" ? "ar" : "en"); setFromDraft(true); }}
                        data-testid={`ai-draft-${i}`}
                        dir="auto"
                        className="text-start text-sm bg-accent/5 text-foreground px-3 py-2 rounded-xl border border-accent/20 hover:bg-accent/10 transition-colors"
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Saved replies quick-pick */}
              {showSaved && (
                <div className="px-4 pt-2 flex flex-wrap gap-2 border-t border-border" data-testid="saved-replies-row">
                  {savedReplies.length === 0 && <span className="text-sm text-muted-foreground py-1">No saved replies. Add some in Settings.</span>}
                  {savedReplies.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => { setReply(s.text); setReplyLang(s.lang); setShowSaved(false); }}
                      data-testid={`saved-reply-pick-${s.id}`}
                      dir="auto"
                      className="text-sm bg-accent/5 text-primary px-3 py-1.5 rounded-full border border-accent/20 hover:bg-accent hover:text-white transition-colors"
                      title={s.text}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Reply box */}
              <div className="p-3 border-t border-border flex items-end gap-2">
                <button
                  onClick={() => setShowSaved((v) => !v)}
                  data-testid="inbox-saved-btn"
                  title={t("quickReplies")}
                  className={`h-11 w-11 shrink-0 grid place-items-center rounded-full transition-colors ${showSaved ? "bg-primary text-white" : "bg-secondary text-primary hover:bg-primary hover:text-white"}`}
                >
                  <MessageSquareQuote size={18} />
                </button>
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder={t("replyPlaceholder")}
                  data-testid="inbox-reply-input"
                  rows={1}
                  dir={replyLang === "ar" ? "rtl" : "ltr"}
                  className="flex-1 resize-none rounded-2xl border border-border px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-accent/40 max-h-28"
                />
                <button
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                  data-testid="inbox-send-btn"
                  className="h-11 w-11 shrink-0 grid place-items-center rounded-full bg-accent text-white disabled:opacity-50 hover:bg-accent/90 transition-colors"
                >
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} className="rtl:rotate-180" />}
                </button>
              </div>
            </>
          )}
        </main>

        {/* CRM panel */}
        <aside className="md:col-span-3 bg-white md:rounded-2xl border-border md:border p-4 max-h-[calc(100vh-6rem)] overflow-y-auto scroll-thin">
          {conv ? (
            <div className="space-y-5">
              <div>
                <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary grid place-items-center text-xl font-semibold">
                  {conv.customer_name?.[0]}
                </div>
                <div className="font-head font-semibold text-lg mt-2 text-foreground">{conv.customer_name}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-1"><Phone size={12} /> {conv.customer_phone}</div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Tag size={13} /> {t("tags")}</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(conv.tags || []).map((tag) => (
                    <span key={tag} className="flex items-center gap-1 text-xs bg-secondary text-primary px-2.5 py-1 rounded-full" data-testid={`tag-${tag}`}>
                      {tag}
                      <button onClick={() => removeTag(tag)} className="hover:text-destructive"><X size={11} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addTag()}
                    placeholder={t("addTag")}
                    data-testid="add-tag-input"
                    className="flex-1 h-9 rounded-lg border border-border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
                  />
                  <button onClick={addTag} data-testid="add-tag-btn" className="h-9 px-3 rounded-lg bg-primary text-white text-sm">+</button>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><TrendingUp size={13} /> {t("leadStatus")}</div>
                <Select value={leadStatus} onValueChange={(v) => { setLeadStatus(v); saveCrm({ lead_status: v }); }}>
                  <SelectTrigger className="h-10" data-testid="lead-status-select">
                    <SelectValue>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[leadStatus]}`}>{statusLabel(leadStatus)}</span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`status-opt-${s}`}>{statusLabel(s)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><CalendarClock size={13} /> {t("appointment")}</div>
                <input
                  type="datetime-local"
                  value={apptAt}
                  onChange={(e) => setApptAt(e.target.value)}
                  data-testid="appt-input"
                  className="w-full h-10 rounded-lg border border-border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
                />
                <input
                  value={apptNote}
                  onChange={(e) => setApptNote(e.target.value)}
                  placeholder={t("appointmentNote")}
                  data-testid="appt-note-input"
                  dir="auto"
                  className="w-full h-10 mt-2 rounded-lg border border-border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
                />
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-muted-foreground">{t("followUp")}:</span>
                  <input
                    type="date"
                    value={followUp}
                    onChange={(e) => setFollowUp(e.target.value)}
                    data-testid="followup-input"
                    className="flex-1 h-9 rounded-lg border border-border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
                  />
                </div>
                <button
                  onClick={() => { saveCrm({ appointment_at: apptAt, appointment_note: apptNote, follow_up_at: followUp }); toast.success(t("saved")); }}
                  data-testid="save-appt-btn"
                  className="mt-2 w-full h-9 rounded-lg bg-secondary text-primary font-medium hover:bg-primary hover:text-white transition-colors text-sm"
                >
                  {t("save")}
                </button>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><MessageSquare size={13} /> {t("notes")}</div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="crm-notes"
                  rows={5}
                  className="w-full rounded-lg border border-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 resize-none"
                />
                <button
                  onClick={() => { saveCrm({ notes }); toast.success(t("saveNotes")); }}
                  data-testid="save-notes-btn"
                  className="mt-2 w-full h-10 rounded-lg bg-secondary text-primary font-medium hover:bg-primary hover:text-white transition-colors text-sm"
                >
                  {t("saveNotes")}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground text-sm grid place-items-center h-full">{t("crm")}</div>
          )}
        </aside>
      </div>
    </div>
  );
}
